import React from "react";
import {App, Button, Checkbox, InputNumber, Modal, Space, Typography, Upload} from "antd";
import {InboxOutlined} from "@ant-design/icons";
import {PDFDocument} from "pdf-lib";

import api from "@/api/axios.js";

const {Text} = Typography;

export default function AddPagesModal({flyerId, open, pageCount, onClose, onCompleted})
{
	const {message} = App.useApp();
	const [file, setFile] = React.useState(null);
	const [sourcePageCount, setSourcePageCount] = React.useState(0);
	const [selectedPages, setSelectedPages] = React.useState([]);
	const [position, setPosition] = React.useState(pageCount + 1);
	const [submitting, setSubmitting] = React.useState(false);

	React.useEffect(() =>
	{
		if (!open)
		{
			setFile(null);
			setSourcePageCount(0);
			setSelectedPages([]);
			setSubmitting(false);
		}
		else
		{
			setPosition(pageCount + 1);
		}
	}, [open, pageCount]);

	// Il conteggio pagine si legge nel browser: evita un viaggio al server solo per
	// sapere quante pagine ha il PDF prima ancora di decidere se importarlo.
	const handleFile = async (nextFile) =>
	{
		// Lo stato si azzera SUBITO, prima ancora di provare a leggere il file, come fa
		// UploadPdfModal. Aggiornarlo solo in caso di successo lascerebbe le caselle e il
		// file da inviare ancorati al PDF precedente mentre il riquadro di trascinamento
		// mostra già il nuovo: si finirebbe per inviare un file diverso da quello mostrato.
		setFile(nextFile);
		setSourcePageCount(0);
		setSelectedPages([]);

		try
		{
			const doc = await PDFDocument.load(await nextFile.arrayBuffer());
			const count = doc.getPageCount();
			setSourcePageCount(count);
			setSelectedPages(Array.from({length: count}, (_, index) => index + 1));
		}
		catch
		{
			setFile(null);
			message.error("PDF non leggibile");
		}

		return false;
	};

	const togglePage = (pageNumber, checked) =>
	{
		setSelectedPages(prev => checked
			? [...prev, pageNumber].sort((a, b) => a - b)
			: prev.filter(value => value !== pageNumber));
	};

	const handleSubmit = async () =>
	{
		if (!file || !selectedPages.length)
		{
			message.warning("Seleziona almeno una pagina");
			return;
		}

		setSubmitting(true);
		try
		{
			const formData = new FormData();
			formData.append("pdf", file, file.name);
			formData.append("pages", selectedPages.join(","));
			formData.append("position", String(Math.max(1, Math.min(position, pageCount + 1)) - 1));

			const {data} = await api.post(`/api/flyers/${flyerId}/pages`, formData);
			message.success(`${data.added} pagine aggiunte`);
			onCompleted?.();
		}
		catch (error)
		{
			message.error(error?.response?.data?.error || "Errore nell'aggiunta delle pagine");
		}
		finally
		{
			setSubmitting(false);
		}
	};

	return (
		<Modal
			open={open}
			title="Aggiungi pagine"
			onCancel={onClose}
			maskClosable={!submitting}
			closable={!submitting}
			footer={[
				<Button key="annulla" onClick={onClose} disabled={submitting}>Annulla</Button>,
				<Button
					key="aggiungi"
					type="primary"
					loading={submitting}
					disabled={!file || !selectedPages.length}
					onClick={handleSubmit}
				>
					Aggiungi {selectedPages.length || ""} pagine
				</Button>
			]}
		>
			<Upload.Dragger accept=".pdf" maxCount={1} beforeUpload={handleFile} showUploadList={!!file}>
				<p className="ant-upload-drag-icon"><InboxOutlined/></p>
				<p className="ant-upload-text">Trascina qui il PDF da cui prendere le pagine</p>
			</Upload.Dragger>

			{sourcePageCount > 0 && (
				<div style={{marginTop: 16}}>
					<Text strong>Pagine da importare</Text>
					<div style={{marginTop: 8, maxHeight: 180, overflowY: "auto"}}>
						<Space wrap>
							{Array.from({length: sourcePageCount}, (_, index) => index + 1).map(pageNumber => (
								<Checkbox
									key={pageNumber}
									checked={selectedPages.includes(pageNumber)}
									onChange={(event) => togglePage(pageNumber, event.target.checked)}
								>
									{pageNumber}
								</Checkbox>
							))}
						</Space>
					</div>

					<div style={{marginTop: 16}}>
						<Text strong>Inserisci a partire dalla posizione</Text>
						<InputNumber
							min={1}
							max={pageCount + 1}
							precision={0}
							value={position}
							onChange={(value) => setPosition(Number(value) || 1)}
							style={{width: "100%", marginTop: 8}}
						/>
						<Text type="secondary" style={{fontSize: 12}}>
							Il volantino ha {pageCount} pagine. Indica {pageCount + 1} per aggiungerle in fondo.
						</Text>
					</div>
				</div>
			)}
		</Modal>
	);
}
